import { Link } from "react-router";
import type { QbOutboxItem, QbStatus } from "@nmt/shared";
import { lineCategories } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { del, errorMessage, get, post } from "../../api";
import { Alert, Badge, Button, Checkbox, Empty, Field, Loading, PageHeader, Panel, PanelHeader, SelectField, formatCents, formatDateTime } from "../../components/ui";
import { useSession } from "../../session";
import { useSettings } from "./useSettings";

type Lists = { accounts: { id: string; name: string; type: string }[]; items: { id: string; name: string }[] };

/** Settings → QuickBooks: connect the sandbox company, map accounts, and review what would be written. */
export function QuickBooksSettings() {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ["qb-status"], queryFn: () => get<QbStatus>("/api/integrations/quickbooks") });
  const result = new URLSearchParams(window.location.search).get("result");
  const disconnect = useMutation({ mutationFn: () => del("/api/integrations/quickbooks"), onSuccess: () => qc.invalidateQueries({ queryKey: ["qb-status"] }) });
  const s = status.data;
  if (status.isLoading || !s) return <Loading />;
  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="QuickBooks" />
      {result === "connected" ? <Alert tone="ok">Connected to QuickBooks.</Alert> : result ? <Alert>{result}</Alert> : null}
      <Panel>
        <PanelHeader
          title="Connection"
          description={!s.configured ? "QuickBooks app keys aren't set up on this server." : s.connected ? `Connected to ${s.companyName ?? "the company"} (${s.environment}) on ${formatDateTime(s.connectedAt)}.` : `Not connected. You'll sign in to Intuit and pick the ${s.environment} company.`}
          action={
            s.configured &&
            (s.connected ? (
              <Button variant="quiet" busy={disconnect.isPending} onClick={() => window.confirm("Disconnect QuickBooks?") && disconnect.mutate()}>
                Disconnect
              </Button>
            ) : (
              <a href="/api/integrations/quickbooks/connect" className="inline-flex items-center rounded-[var(--radius-control)] bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-deep">
                Connect QuickBooks
              </a>
            ))
          }
        />
        <div className="flex flex-col gap-2 px-5 py-4 text-sm">
          {s.lastError && <Alert>{s.lastError}</Alert>}
          <div className="flex items-center gap-2">
            <span className="font-semibold">Writes:</span>
            {s.writesEnabled ? <Badge tone="brand">On: changes are sent to QuickBooks</Badge> : <Badge tone="ok">Off: nothing is sent; each write is recorded below as a dry run</Badge>}
          </div>
          <p className="text-muted">Writes are switched on in the server setup (not here), on purpose. Reading QuickBooks (accounts, items) always works once connected.</p>
          {!s.connected && s.configured && <p className="text-muted">The Intuit app needs this redirect address in its settings: <code className="rounded bg-mist px-1">{s.redirectUri}</code></p>}
        </div>
      </Panel>
      {s.connected && (
        <Alert tone="info">
          Linking records, invoices, payments, and expenses from QuickBooks are all on{" "}
          <Link to="/settings/sync" className="font-semibold underline">
            Settings → Sync
          </Link>
          .
        </Alert>
      )}
      {s.connected && <Mapping />}
      {s.connected && <PnlSettings />}
      <Outbox />
    </div>
  );
}

function Mapping() {
  const { me } = useSession();
  const { query, save } = useSettings();
  const lists = useQuery({ queryKey: ["qb-lists"], queryFn: () => get<Lists>("/api/integrations/quickbooks/lists") });
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState<{ itemId: string; retainageItemId: string; depositAccountId: string; paidFromAccountId: string; expense: Record<string, string>; payFrom: { accountId: string; last4: string }[]; discountAccountId: string; takeDiscounts: boolean } | null>(null);
  useEffect(() => {
    const st = query.data?.settings;
    if (st && !form) {
      const payFrom = st["quickbooks.paymentAccounts"].map((a) => ({ accountId: a.accountId, last4: a.last4 }));
      // The default account is always on the list.
      const def = st["quickbooks.paidFromAccountId"];
      if (def && !payFrom.some((a) => a.accountId === def)) payFrom.unshift({ accountId: def, last4: "" });
      setForm({ itemId: st["quickbooks.itemId"] ?? "", retainageItemId: st["quickbooks.retainageItemId"] ?? "", depositAccountId: st["quickbooks.depositAccountId"] ?? "", paidFromAccountId: def ?? "", expense: st["quickbooks.expenseAccounts"], payFrom, discountAccountId: st["quickbooks.discountAccountId"] ?? "", takeDiscounts: st["receipts.takeEarlyDiscounts"] });
    }
  }, [query.data, form]);
  if (lists.error) return <Alert>{errorMessage(lists.error)}</Alert>;
  if (!lists.data || !form) return <Loading />;
  const { accounts, items } = lists.data;
  const pick = (label: string, value: string, onChange: (v: string) => void, filter: (t: string) => boolean, opts?: { id: string; name: string }[]) => (
    <SelectField label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Not set</option>
      {(opts ?? accounts.filter((a) => filter(a.type))).map((a) => (
        <option key={a.id} value={a.id}>
          {a.name}
        </option>
      ))}
    </SelectField>
  );
  return (
    <Panel>
      <PanelHeader title="Where things go" description="Pick the QuickBooks item and accounts the CRM uses. Anything not set waits in the outbox." />
      <form
        className="flex flex-col gap-4 px-5 py-4"
        onSubmit={(e) => {
          e.preventDefault();
          setNotice(null);
          save.mutate(
            {
              "quickbooks.itemId": form.itemId || null,
              "quickbooks.retainageItemId": form.retainageItemId || null,
              "quickbooks.depositAccountId": form.depositAccountId || null,
              "quickbooks.paidFromAccountId": form.paidFromAccountId || form.payFrom.find((p) => p.accountId)?.accountId || null,
              "quickbooks.expenseAccounts": Object.fromEntries(Object.entries(form.expense).filter(([, v]) => v)),
              "quickbooks.discountAccountId": form.discountAccountId || null,
              "receipts.takeEarlyDiscounts": form.takeDiscounts,
              "quickbooks.paymentAccounts": form.payFrom
                .filter((p) => p.accountId)
                .map((p) => {
                  const a = accounts.find((x) => x.id === p.accountId);
                  return { accountId: p.accountId, name: a?.name ?? p.accountId, type: a?.type === "Credit Card" ? ("card" as const) : ("bank" as const), last4: p.last4.trim() };
                }),
            },
            { onSuccess: () => setNotice("Saved.") },
          );
        }}
      >
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        {notice && <Alert tone="ok">{notice}</Alert>}
        <div className="grid gap-4 sm:grid-cols-3">
          {pick("Item on estimates and invoices", form.itemId, (v) => setForm({ ...form, itemId: v }), () => true, items)}
          {pick("Retainage item (progress billing)", form.retainageItemId, (v) => setForm({ ...form, retainageItemId: v }), () => true, items)}
          {pick("Payments go to", form.depositAccountId, (v) => setForm({ ...form, depositAccountId: v }), (t) => ["Bank", "Other Current Asset"].includes(t))}
        </div>
        <div>
          <div className="mb-1 text-sm font-semibold text-ink-soft">Paid expenses come out of</div>
          <p className="mb-2 text-sm text-muted">Checking and each company card. On an expense, the receipt reader picks the card by its last 4 digits (or uses the default for cash, checks, and unknown); people can change it.</p>
          <ul className="flex flex-col gap-2">
            {form.payFrom.map((p, i) => (
              <li key={i} className="grid grid-cols-[1fr_96px_auto_auto] items-end gap-2">
                <SelectField label={i ? "" : "Account"} aria-label="Account" value={p.accountId} onChange={(e) => setForm({ ...form, payFrom: form.payFrom.map((x, k) => (k === i ? { ...x, accountId: e.target.value } : x)) })}>
                  <option value="">Pick an account</option>
                  {accounts
                    .filter((a) => a.type === "Bank" || a.type === "Credit Card")
                    .map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name} ({a.type === "Bank" ? "bank" : "card"})
                      </option>
                    ))}
                </SelectField>
                <Field label={i ? "" : "Card last 4"} aria-label="Card last 4" inputMode="numeric" maxLength={4} placeholder="1234" value={p.last4} onChange={(e) => setForm({ ...form, payFrom: form.payFrom.map((x, k) => (k === i ? { ...x, last4: e.target.value.replace(/\D/g, "") } : x)) })} />
                <label className="mb-2.5 flex items-center gap-1.5 text-sm">
                  <input type="radio" name="paid-from-default" checked={!!p.accountId && form.paidFromAccountId === p.accountId} disabled={!p.accountId} onChange={() => setForm({ ...form, paidFromAccountId: p.accountId })} className="accent-[var(--color-brand)]" />
                  Default
                </label>
                <button type="button" className="mb-2.5 text-muted hover:text-danger" aria-label="Remove" onClick={() => setForm({ ...form, payFrom: form.payFrom.filter((_, k) => k !== i), paidFromAccountId: form.paidFromAccountId === p.accountId ? "" : form.paidFromAccountId })}>
                  ×
                </button>
              </li>
            ))}
          </ul>
          <button type="button" className="mt-2 text-sm font-semibold text-brand hover:underline" onClick={() => setForm({ ...form, payFrom: [...form.payFrom, { accountId: "", last4: "" }] })}>
            Add an account
          </button>
        </div>
        <div>
          <div className="mb-2 text-sm font-semibold text-ink-soft">Expense category → account</div>
          <div className="grid gap-3 sm:grid-cols-2">
            {lineCategories(me?.lists.expenseCategories ?? []).map((c) => (
              <div key={c}>{pick(c, form.expense[c] ?? "", (v) => setForm({ ...form, expense: { ...form.expense, [c]: v } }), (t) => ["Expense", "Cost of Goods Sold", "Other Expense"].includes(t))}</div>
            ))}
          </div>
        </div>
        <div>
          <div className="mb-2 text-sm font-semibold text-ink-soft">Early-pay discounts</div>
          <div className="grid gap-3 sm:grid-cols-2">
            {pick("Discounts taken go to", form.discountAccountId, (v) => setForm({ ...form, discountAccountId: v }), (t) => ["Income", "Other Income", "Cost of Goods Sold", "Expense", "Other Expense"].includes(t))}
            <div className="sm:pt-7">
              <Checkbox label="Take every early-pay discount" description="A bill offering one (“You may deduct 6.80 if paid by 11/30”) gets a vendor credit for it to this account, on no job, and is due by the discount's last day. Untick it on any bill." checked={form.takeDiscounts} onChange={(takeDiscounts) => setForm({ ...form, takeDiscounts })} />
            </div>
          </div>
        </div>
        <div>
          <Button type="submit" variant="primary" busy={save.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Panel>
  );
}


/** Links CRM records to customers, jobs, and vendors already in QuickBooks, so the sync doesn't duplicate them. */
/** The job P&L tab: which accounts are "Job materials" (the rest is labor), and overhead's share of income. */
function PnlSettings() {
  const { query, save } = useSettings();
  const lists = useQuery({ queryKey: ["qb-lists"], queryFn: () => get<Lists>("/api/integrations/quickbooks/lists") });
  const [picked, setPicked] = useState<string[] | null>(null);
  const [commission, setCommission] = useState<string[] | null>(null);
  const [overhead, setOverhead] = useState<string | null>(null);
  const st = query.data?.settings;
  if (!st || !lists.data) return null;
  const chosen = picked ?? st["quickbooks.materialAccountIds"];
  const commissionChosen = commission ?? st["quickbooks.commissionAccountIds"];
  const pct = overhead ?? String(st["reports.overheadPercent"]);
  const expenseAccounts = lists.data.accounts.filter((a) => ["Cost of Goods Sold", "Expense", "Other Expense"].includes(a.type));
  const auto = expenseAccounts.filter((a) => /material/i.test(a.name)).map((a) => a.name);
  const autoCommission = expenseAccounts.filter((a) => /admin\s*sub-?contract/i.test(a.name)).map((a) => a.name);
  const n = Number(pct);
  return (
    <Panel>
      <PanelHeader title="Job P&L" description="The P&L tab on a job: QuickBooks's income and expenses for the job. Expenses in the job materials accounts are “Job materials”; every other expense is “Labor”. Overhead is added as a share of income. Commission is shown below net profit." />
      <div className="flex flex-col gap-4 px-5 py-4 text-sm">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <div>
          <div className="mb-1 font-semibold text-ink-soft">Job materials accounts</div>
          <p className="mb-2 text-muted">{chosen.length ? "These accounts:" : `None picked, so any account named “materials” counts${auto.length ? ` (now: ${auto.join(", ")})` : ""}.`}</p>
          <div className="grid gap-1 sm:grid-cols-2">
            {expenseAccounts.map((a) => (
              <Checkbox key={a.id} label={`${a.name} (${a.type})`} checked={chosen.includes(a.id)} onChange={(on) => setPicked(on ? [...chosen, a.id] : chosen.filter((x) => x !== a.id))} />
            ))}
          </div>
        </div>
        <div>
          <div className="mb-1 font-semibold text-ink-soft">Commission accounts</div>
          <p className="mb-2 text-muted">
            Commission is figured on net profit, so these are kept out of it and shown below it. {commissionChosen.length ? "These accounts:" : `None picked, so any account named “Admin Subcontracting” counts${autoCommission.length ? ` (now: ${autoCommission.join(", ")})` : ""}.`}
          </p>
          <div className="grid gap-1 sm:grid-cols-2">
            {expenseAccounts.map((a) => (
              <Checkbox key={a.id} label={`${a.name} (${a.type})`} checked={commissionChosen.includes(a.id)} onChange={(on) => setCommission(on ? [...commissionChosen, a.id] : commissionChosen.filter((x) => x !== a.id))} />
            ))}
          </div>
        </div>
        <Field label="Overhead, % of income" type="number" min={0} max={100} step="0.5" value={pct} onChange={(e) => setOverhead(e.target.value)} className="w-40" />
        <div>
          <Button
            variant="primary"
            disabled={!Number.isFinite(n) || n < 0 || n > 100}
            busy={save.isPending}
            onClick={() => save.mutate({ "quickbooks.materialAccountIds": chosen, "quickbooks.commissionAccountIds": commissionChosen, "reports.overheadPercent": n }, { onSuccess: () => (setPicked(null), setCommission(null), setOverhead(null)) })}
          >
            Save
          </Button>
        </div>
      </div>
    </Panel>
  );
}

/** JobNimbus invoices that don't line up with QuickBooks's, by number; each says why, and a job not linked yet can be linked from here. */
const STATUS_TONE = { dry_run: "wait", sent: "ok", failed: "brand", skipped: "off" } as const;
const STATUS_LABEL = { dry_run: "Dry run", sent: "Sent", failed: "Failed", skipped: "Waiting" } as const;

function Outbox() {
  const q = useQuery({ queryKey: ["qb-outbox"], queryFn: () => get<QbOutboxItem[]>("/api/integrations/quickbooks/outbox") });
  const [open, setOpen] = useState<string | null>(null);
  return (
    <Panel>
      <PanelHeader title="Outbox" description="What the CRM sent to QuickBooks, or (with writes off) would have sent. Newest first." />
      {q.isLoading ? <div className="px-5"><Loading /></div> : !q.data?.length ? <Empty>Nothing yet. Signing a proposal, sending an invoice, recording a payment, or approving a receipt adds to it.</Empty> : (
        <ul className="text-sm">
          {q.data.map((o) => (
            <li key={o.id} className="border-b border-mist px-5 py-2 last:border-0">
              <button className="flex w-full flex-wrap items-center gap-2 text-left" onClick={() => setOpen(open === o.id ? null : o.id)}>
                <Badge tone={STATUS_TONE[o.status]}>{STATUS_LABEL[o.status]}</Badge>
                <span className="font-medium capitalize">{o.entity}</span>
                <span className="min-w-0 flex-1 truncate">{o.label}</span>
                {o.qbId && <span className="text-muted">QuickBooks #{o.qbId}</span>}
                <span className="text-muted">{formatDateTime(o.createdAt)}</span>
              </button>
              {o.error && <p className="text-danger">{o.error}</p>}
              {open === o.id && <pre className="mt-2 max-h-80 overflow-auto rounded bg-mist p-3 text-xs">{JSON.stringify(o.payload, null, 2)}</pre>}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
